import * as shopify from '@bob/shopify-services'
import * as url from '@bob/url'
import * as utils from '@bob/utils'

import * as base from '../base'
import * as cold from '../cold'
import * as tag from '../tag'
import { buildTags } from '../utils'

export type MediumProduct = {
    availableForSale: boolean
    brand: string
    compareAtPriceRange: [number, number] | null
    category: base.Taxonomy | null
    criteria: {
        environmental: tag.PillarMediumTag[]
        social: tag.PillarMediumTag[]
    }
    description: string
    id: string
    handle: string
    href: string
    moments: tag.MomentMediumTag[]
    picture: base.Image
    priceRange: [number, number]
    tags: tag.PartialTag[]
    title: string
    vendor: string
}

export function mediumProduct(
    product: shopify.product.MediumProduct,
    cold: {
        categories: cold.SlimColdData['categories']
        tags: cold.SlimColdData['tags']
        taxonomies: cold.SlimColdData['taxonomies']
    }
): MediumProduct {
    const {
        categories,
        environmentalPillars,
        moments,
        productTypes,
        socialPillars,
        others
    } = buildTags(
        product.tags.map(tag => utils.text.slugify(tag)),
        cold
    )

    function formatProductTags() {
        return [
            // Types
            ...categories.map(tag => formatTag(tag, 'category')),
            ...productTypes.map(tag => formatTag(tag, 'types')),
            // Other tags
            ...environmentalPillars.map(tag => formatTag(tag, 'environmentalPillar')),
            ...socialPillars.map(tag => formatTag(tag, 'socialPillar')),
            ...moments.map(tag => formatTag(tag, 'moments')),
            ...others
        ]
    }

    return {
        availableForSale: product.availableForSale,
        brand: product.brandName?.value ?? product.vendor,
        category: categories.length > 0 ? formatTag(categories[0], 'category') : null,
        compareAtPriceRange: getCompareAtPriceRange(product),
        criteria: {
            environmental: environmentalPillars || [],
            social: socialPillars || []
        },
        description: product.descriptionHtml,
        handle: product.handle,
        href: url.product({
            handle: product.handle,
            id: shopify.base.shopifyNumericId(product.id, 'Product'),
            title: product.title
        }).path,
        id: product.id,
        moments,
        picture: {
            alt: product.images?.edges[0]?.node.altText || '',
            src: product.images?.edges[0]?.node.originalSrc || '',
            srcSet: `${product.images?.edges[0]?.node.transformedSrc1x} 1x, ${product.images?.edges[0]?.node.transformedSrc2x} 2x, ${product.images?.edges[0]?.node.transformedSrc3x} 3x`,
            origin: product.images?.edges[0]?.node.altText || '',
            width: null,
            height: null
        },
        priceRange: product.priceRange
            ? [
                  Number(product.priceRange.minVariantPrice.amount),
                  Number(product.priceRange.maxVariantPrice.amount)
              ]
            : [0, 0],
        tags: formatProductTags(),
        title: product.title,
        vendor: product.vendor
    }
}

function getCompareAtPriceRange(
    product: shopify.product.MediumProduct
): [number, number] | null {
    const minVariantPriceAmount = Number(product.priceRange.minVariantPrice.amount)

    const minVariantCompareAtPriceAmount = parseFloat(
        product.compareAtPriceRange.minVariantPrice.amount
    )
    const maxVariantCompareAtPriceAmount = parseFloat(
        product.compareAtPriceRange.maxVariantPrice.amount
    )

    if (minVariantCompareAtPriceAmount === 0 && maxVariantCompareAtPriceAmount === 0)
        return null

    if (minVariantPriceAmount >= minVariantCompareAtPriceAmount) {
        // if (minVariantPriceAmount !== minVariantCompareAtPriceAmount) {
        //     logger.warn(
        //         `[Shopify] variants minimum 'price' is higher than its minimum 'compareAtPrice' for product ${product.id}`,
        //         {
        //             product,
        //             values: {
        //                 minPrice: minVariantPriceAmount,
        //                 minCompareAtPrice: minVariantCompareAtPriceAmount
        //             }
        //         }
        //     )
        // }
        return null
    }

    return [minVariantCompareAtPriceAmount, maxVariantCompareAtPriceAmount]
}

function formatTag<T extends base.Taxonomy>(tag: T, kind: string): base.Tag {
    return {
        kind,
        name: tag.name,
        slug: tag.slug
    }
}
